/**
 * Profile Service
 * Manages user profile data including Google profile pictures
 */

import { supabase } from '../lib/supabase';
// TEMPORARILY COMMENTED: Google integration - will restore later
// import GmailOAuthService from './gmail/GmailOAuthService';

// Cache for profile pictures to prevent duplicate requests
const profilePictureCache = new Map();
const pendingRequests = new Map();

// TEMPORARILY COMMENTED: Google integration - will restore later
/**
 * Update user profile with Google profile picture
 * @param {string} userId - Supabase user ID
 * @param {string} googleAccessToken - Google OAuth access token
 * @returns {Object} Result object with success status and data/error
 */
/* export const updateProfilePictureFromGoogle = async (userId, googleAccessToken) => {
  try {
    // 1. Fetch profile from Google
    const tokens = { access_token: googleAccessToken };
    const googleProfile = await GmailOAuthService.getUserProfile(tokens);
    
    if (!googleProfile || !googleProfile.picture) {
      console.warn('No profile picture available from Google');
      return { success: false, error: 'No picture available' };
    }

    // 2. Get high-res version (400px)
    const highResPicture = GmailOAuthService.getHighResProfilePicture(googleProfile.picture, 400);

    // 3. Update Supabase profile with avatar_url
    const { data, error } = await supabase
      .from('profiles')
      .update({
        avatar_url: highResPicture
      })
      .eq('id', userId)
      .select();

    if (error) {
      console.error('Failed to update profile picture:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ Profile picture updated successfully');
    
    // Clear cache for this user so new picture is fetched immediately
    clearProfilePictureCache(userId);
    
    return { success: true, data: data[0] };
  } catch (error) {
    console.error('Error updating profile picture:', error);
    return { success: false, error: error.message };
  }
}; */

/**
 * Get user's current profile picture
 * @param {string} userId - Supabase user ID
 * @returns {string|null} Profile picture URL or null
 */
export const getProfilePicture = async (userId) => {
  try {
    if (!userId) {
      return null;
    }

    // Check cache first
    if (profilePictureCache.has(userId)) {
      return profilePictureCache.get(userId);
    }

    // Check if request is already pending for this user
    if (pendingRequests.has(userId)) {
      return await pendingRequests.get(userId);
    }

    // Create new request
    const requestPromise = (async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('avatar_url, updated_at')
          .eq('id', userId)
          .single();

        if (error || !data) {
          return null;
        }

        const url = data.avatar_url;
        // Cache the result (even if null, to avoid repeated failed requests)
        profilePictureCache.set(userId, url);
        return url;
      } catch (error) {
        console.error('Error fetching profile picture:', error);
        return null;
      } finally {
        // Remove from pending requests
        pendingRequests.delete(userId);
      }
    })();

    // Store pending request to prevent duplicates
    pendingRequests.set(userId, requestPromise);
    return await requestPromise;
  } catch (error) {
    console.error('Error fetching profile picture:', error);
    pendingRequests.delete(userId);
    return null;
  }
};

/**
 * Clear profile picture cache for a specific user
 * Useful when profile picture is updated
 * @param {string} userId - Supabase user ID
 */
export const clearProfilePictureCache = (userId) => {
  if (userId) {
    profilePictureCache.delete(userId);
  }
};

/**
 * Clear all profile picture cache
 * Useful for testing or cache invalidation
 */
export const clearAllProfilePictureCache = () => {
  profilePictureCache.clear();
  pendingRequests.clear();
};

/**
 * Upload profile picture from file
 * @param {string} userId - Supabase user ID
 * @param {File} file - Image file to upload
 * @returns {Object} Result object with success status and data/error
 */
export const uploadProfilePicture = async (userId, file) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    if (!file) {
      return { success: false, error: 'File is required' };
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      return { success: false, error: 'Please select an image file' };
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      return { success: false, error: 'Image size must be less than 2MB' };
    }

    // Import UploadFile dynamically to avoid circular dependencies
    const { UploadFile } = await import('../api/integrations');
    
    // Create unique file path: {userId}/avatars/{timestamp}_{sanitized_filename}
    const timestamp = Date.now();
    
    // Sanitize filename: remove special characters, replace spaces with underscores
    const sanitizeFileName = (name) => {
      if (!name) return 'photo';
      return name
        .normalize('NFD') // Normalize unicode characters
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
        .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace non-alphanumeric (except . and -) with underscore
        .replace(/[\s\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, '_') // Replace all types of spaces with underscore
        .replace(/_{2,}/g, '_') // Replace multiple underscores with single
        .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
        .toLowerCase();
    };
    
    // Get file extension
    const lastDot = file.name.lastIndexOf('.');
    const fileExtension = lastDot > 0 ? file.name.substring(lastDot + 1) : 'jpg';
    const baseName = lastDot > 0 ? file.name.substring(0, lastDot) : file.name;
    
    const sanitizedOriginalName = sanitizeFileName(baseName) || 'photo';
    const fileName = `${timestamp}_${sanitizedOriginalName}.${fileExtension}`;
    const filePath = `${userId}/avatars/${fileName}`;
    
    // Upload to Supabase uploads bucket
    const uploadResult = await UploadFile(file, 'uploads', filePath);
    
    if (!uploadResult || !uploadResult.publicUrl) {
      return { success: false, error: 'Upload failed - no URL returned' };
    }

    // Update Supabase profile with avatar_url
    const { data, error } = await supabase
      .from('profiles')
      .update({
        avatar_url: uploadResult.publicUrl
      })
      .eq('id', userId)
      .select();

    if (error) {
      console.error('Failed to update profile picture:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ Profile picture uploaded successfully');
    
    // Clear cache for this user so new picture is fetched immediately
    clearProfilePictureCache(userId);
    
    return { success: true, data: data[0], url: uploadResult.publicUrl };
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    return { success: false, error: error.message };
  }
};
